import { useNavigate } from '@tanstack/react-router';
import { motion, useReducedMotion } from 'motion/react';
import { Button, Sticker } from '@/components/ui';

const DO = [
  {
    emoji: '🎯',
    text: 'Compliment something specific — the jacket, the dance moves, the playlist.',
  },
  { emoji: '😌', text: 'Keep it PG until you both clearly want otherwise.' },
  { emoji: '🤐', text: 'Let silence be an answer. No follow-up pestering.' },
];
const DONT = [
  { emoji: '🙅', text: 'Comments about someone’s body. Just don’t.' },
  { emoji: '🔢', text: '“Give me your number” in the first line.' },
  { emoji: '📍', text: 'Pushing to meet in private, or move off Nearmirer.' },
  { emoji: '😬', text: 'Anything you wouldn’t say to their face.' },
];

function TipCard({
  emoji,
  text,
  flavor,
  tilt,
  delay,
}: {
  emoji: string;
  text: string;
  flavor: 'melon' | 'berry';
  tilt: number;
  delay: number;
}) {
  const reduce = useReducedMotion();
  return (
    <motion.div
      initial={{ opacity: 0, y: 16, rotate: 0 }}
      animate={{ opacity: 1, y: 0, rotate: tilt }}
      whileHover={reduce ? undefined : { rotate: 0, scale: 1.02 }}
      whileTap={{ scale: 0.97 }}
      transition={{ type: 'spring', stiffness: 260, damping: 20, delay }}
      className={`flex items-start gap-3 rounded-tile border-2 border-ink p-3 shadow-hard ${
        flavor === 'melon' ? 'bg-flavor-melon-soft' : 'bg-flavor-berry-soft'
      }`}
    >
      <motion.span
        className="text-2xl"
        animate={reduce ? undefined : { rotate: [-6, 6, -6] }}
        transition={{ duration: 3, repeat: Infinity, ease: 'easeInOut', delay }}
        aria-hidden
      >
        {emoji}
      </motion.span>
      <p className="text-sm text-ink-soft">{text}</p>
    </motion.div>
  );
}

export function OnboardingHowToPage() {
  const navigate = useNavigate();
  return (
    <div className="flex flex-col gap-6">
      <div>
        <Sticker flavor="citron" rotate={-3}>
          setup · 2 / 4
        </Sticker>
        <h1 className="mt-4 text-3xl font-semibold">How to Spark</h1>
        <p className="mt-2 text-sm text-ink-soft">
          Anonymity is a feature, not a shield. Talk like they can see your face.
        </p>
      </div>

      <div>
        <Sticker flavor="melon" rotate={-2} interactive={false} className="mb-3">
          the good stuff
        </Sticker>
        <div className="flex flex-col gap-2.5">
          {DO.map((t, i) => (
            <TipCard
              key={t.text}
              emoji={t.emoji}
              text={t.text}
              flavor="melon"
              tilt={i % 2 ? 1.5 : -1.5}
              delay={i * 0.08}
            />
          ))}
        </div>
      </div>

      <div>
        <Sticker flavor="berry" rotate={2} interactive={false} className="mb-3">
          hard nos
        </Sticker>
        <div className="flex flex-col gap-2.5">
          {DONT.map((t, i) => (
            <TipCard
              key={t.text}
              emoji={t.emoji}
              text={t.text}
              flavor="berry"
              tilt={i % 2 ? -1.5 : 1.5}
              delay={0.3 + i * 0.08}
            />
          ))}
        </div>
      </div>

      <p className="text-xs text-ink-faint">
        Break these and you’ll get a warning, then a mute, then a ban. Report anyone who breaks them
        toward you — it goes to a real person.
      </p>

      <Button variant="candy" size="lg" onClick={() => navigate({ to: '/onboarding/location' })}>
        Got it
      </Button>
    </div>
  );
}
